import type { GuestQuery } from '@dx-ui/framework-auth-provider';
import { ServerAuthProvider, ServerFirstAuthClient } from '@dx-ui/framework-auth-provider';
import { logError } from '@dx-ui/framework-logger';
import type { DefaultOptions, MutationFunction, QueryKey } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type * as React from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { makeDefaultMutationFn, makeDefaultQueryFn } from './default-fetch-fn';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type ServerFirstAuthArgs = {
  appId: string;
  appName: string;
  guestInfo: GuestQuery | null;
  isAuthenticated: boolean;
};

export type MakeServerFirstQueryClientArgs = ServerFirstAuthArgs & {
  /**
   * Operation names allowed for client-side mutations that use the default mutation function;
   * others are rejected and logged (in production too). Mutations with their own `mutationFn`
   * aren't checked. Pass `[]` to allow none. Prefer React Router actions for app-owned mutations.
   * Read once when the client is created; changes after mount are ignored.
   */
  allowedClientMutations: readonly string[];
  reactQueryClientOverrideOptions?: DefaultOptions['queries'];
};

/**
 * Checks that one of the document's operation names is allowlisted. Returns null if so; otherwise
 * logs `CLIENT_MUTATION_NOT_ALLOWED` and returns the error. Shared by every client-side mutation entry point.
 */
export function checkClientMutationAllowed(
  document: unknown,
  appName: string,
  allowedClientMutations: ReadonlySet<string>
): Error | null {
  const { operationName, originalOpName } =
    document && typeof document === 'object'
      ? (document as { operationName?: string; originalOpName?: string })
      : {};
  const names = [operationName, originalOpName].filter((name): name is string => !!name);
  if (names.some((name) => allowedClientMutations.has(name))) return null;
  const error = new Error(
    `Client-side mutation "${names[0] ?? 'unknown'}" is not allowed in ${appName}. ` +
      "Use a server-side action (e.g. a React Router action), or add it to the app's client mutation allowlist."
  );
  // Logged so a newly added client mutation surfaces in monitoring instead of failing silently.
  logError('CLIENT_MUTATION_NOT_ALLOWED', error);
  return error;
}

function withMutationAllowlist(
  mutationFn: MutationFunction,
  appName: string,
  allowedClientMutations: readonly string[]
): MutationFunction {
  const allowed = new Set(allowedClientMutations);
  return (params) => {
    const document: unknown = Array.isArray(params) ? params[0] : undefined;
    const error = checkClientMutationAllowed(document, appName, allowed);
    return error ? Promise.reject(error) : mutationFn(params);
  };
}

/**
 * QueryClient for server-first apps whose OSC components still use React Query client-side.
 * Uses `ServerFirstAuthClient`, so it never fetches guest info in the browser.
 */
export function makeServerFirstQueryClient({
  allowedClientMutations,
  appId,
  appName,
  guestInfo,
  isAuthenticated,
  reactQueryClientOverrideOptions = {},
}: MakeServerFirstQueryClientArgs) {
  const authClient = new ServerFirstAuthClient({ appId, appName, guestInfo, isAuthenticated });
  const defaultMutationFn = makeDefaultMutationFn({ appName, authClient });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: makeDefaultQueryFn({ appName, authClient }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: false,
        ...reactQueryClientOverrideOptions,
      },
      mutations: {
        mutationFn: withMutationAllowlist(defaultMutationFn, appName, allowedClientMutations),
      },
    },
  });

  return { queryClient, authClient };
}

export type InitialQueryData = ReadonlyArray<{ queryKey: QueryKey; data: unknown }>;

function seedQueryData(queryClient: QueryClient, initialQueryData?: InitialQueryData) {
  for (const { queryKey, data } of initialQueryData ?? []) {
    queryClient.setQueryData<unknown>(queryKey, data);
  }
}

export type ServerFirstQueryProviderProps = Omit<ServerFirstAuthArgs, 'guestInfo'> &
  Pick<MakeServerFirstQueryClientArgs, 'allowedClientMutations'> & {
    children: React.ReactNode;
    guestInfo: GuestQuery['guest'] | null;
    /** Loader data seeded into the cache so OSC queries reading it don't refetch client-side. Must be referentially stable (`useMemo` unless the app uses React Compiler). */
    initialQueryData?: InitialQueryData;
    /** Called after another tab signs out (e.g. reload so this tab renders as anonymous). */
    onCrossTabLogout?: () => void;
    reactQueryClientOverrideOptions?: DefaultOptions['queries'];
  };

/**
 * Server-first counterpart to `QueryProvider`: provides a React Query client and
 * `useAuth()` context from loader data, without client-side guest fetches.
 */
export function ServerFirstQueryProvider({
  allowedClientMutations,
  appId,
  appName,
  children,
  guestInfo,
  initialQueryData,
  isAuthenticated,
  onCrossTabLogout,
  reactQueryClientOverrideOptions,
}: ServerFirstQueryProviderProps) {
  const guestQuery = useMemo(() => (guestInfo ? { guest: guestInfo } : null), [guestInfo]);
  const [{ queryClient, authClient }] = useState(() => {
    const clients = makeServerFirstQueryClient({
      allowedClientMutations,
      appId,
      appName,
      guestInfo: guestQuery,
      isAuthenticated,
      reactQueryClientOverrideOptions,
    });
    // Seed before first render so OSC queries reading it don't fetch on mount or during SSR.
    seedQueryData(clients.queryClient, initialQueryData);
    return clients;
  });

  const guestId = guestInfo?.guestId ?? null;
  const authIdentityRef = useRef({ guestId, isAuthenticated });
  const seededInitialQueryDataRef = useRef(initialQueryData);

  // Layout effect so the client is synced before children's effects fire queries/mutations.
  useIsomorphicLayoutEffect(() => {
    authClient.setAuthState({ guestInfo: guestQuery, isAuthenticated });
  }, [authClient, guestQuery, isAuthenticated]);

  // Apps may switch auth without a full reload, so drop the previous guest's cached data on identity change.
  useIsomorphicLayoutEffect(() => {
    const previous = authIdentityRef.current;
    if (previous.guestId === guestId && previous.isAuthenticated === isAuthenticated) return;
    authIdentityRef.current = { guestId, isAuthenticated };
    queryClient.getMutationCache().clear();
    queryClient.removeQueries({ type: 'inactive' });
    // Reset (not remove) mounted queries: removal doesn't notify their observers, so they'd keep showing old data.
    void queryClient.resetQueries();
    seededInitialQueryDataRef.current = initialQueryData;
    seedQueryData(queryClient, initialQueryData);
  }, [queryClient, guestId, isAuthenticated, initialQueryData]);

  // Pushes revalidated loader data into the cache for the same guest.
  useEffect(() => {
    if (seededInitialQueryDataRef.current === initialQueryData) return;
    seededInitialQueryDataRef.current = initialQueryData;
    seedQueryData(queryClient, initialQueryData);
  }, [queryClient, initialQueryData]);

  return (
    <QueryClientProvider client={queryClient}>
      <ServerAuthProvider
        authClient={authClient}
        guestInfo={guestInfo}
        isAuthenticated={isAuthenticated}
        onCrossTabLogout={onCrossTabLogout}
      >
        {children}
      </ServerAuthProvider>
    </QueryClientProvider>
  );
}
